import React, {useEffect, useState} from 'react'
import PrimaryButton from '../../components/common/buttons/PrimaryButton'
import {FaInfoCircle, FaExternalLinkAlt, FaSpinner} from 'react-icons/fa'
import {
  isUserInactive,
  isUserOnTrial,
  hasUserBillingAccess,
  isUserTerminated,
  isUserFailed,
  isUserPastDue,
} from '../../helper/userHelpers'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {toast} from 'react-toastify'
import {NavLink} from 'react-router-dom'

const BillingDetails = ({
  currentUser,
  isLoading,
  // paymentMethods,
  // defaultPM,
  // currentInvoices,
  // indexOfFirstInvoice,
  // currentPage,
  // totalPages,
  // setCurrentPage,
  // upcomingInvoice,
  // clientSecret,
  // stripePromise,
  isActive,
  isCancelled,
  isFailed: propIsFailed,
  isCancelling,
  openCancelModal,
  subscriptionEndDate,
  fetchBillingData,
  // handleStartSubscription,
  // handleResubscribe,
  // isLoading,
  activePatientCount,
  deviceCount,
}) => {
  const [isManagingBilling, setIsManagingBilling] = useState(false)
  const [loading , setLoading] = useState(false)
  // Use the helper functions as fallbacks if props aren't provided
  const isTrial = isUserOnTrial(currentUser)
  const isTerminated = isUserTerminated(currentUser)
  const isInactive = isUserInactive(currentUser)
  const isFailed = propIsFailed || isUserFailed(currentUser) // Use prop if available, fallback to helper
  const isPastDue = isUserPastDue(currentUser)
  const isNeverSubscribed = !currentUser?.subscription_status

  useEffect(() => {
    setTimeout(() => {
      // Fetch billing data after a short delay to ensure UI is ready
    setLoading(isLoading)
    }, 1000)
  }, [isLoading])
  // Create mock preview invoice for trial/new/cancelled/terminated users when no upcomingInvoice exists
  // const shouldCreatePreviewInvoice =
  //   (isTrial || isNeverSubscribed || isCancelled || isTerminated) &&
  //   (!upcomingInvoice || Object.keys(upcomingInvoice).length === 0) &&
  //   activePatientCount > 0

  // const previewInvoice = shouldCreatePreviewInvoice
  //   ? {
  //       created: Math.floor(Date.now() / 1000), // Current timestamp
  //       lines: {
  //         data: [
  //           {
  //             id: 'preview-1',
  //             description: 'Patient Monitoring Service',
  //             quantity: activePatientCount,
  //             price: {
  //               unit_amount: 1000, // $10.00 in cents
  //             },
  //           },
  //         ],
  //       },
  //       subtotal: activePatientCount * 1000, // in cents
  //       total: activePatientCount * 1000, // in cents
  //       amount_due: activePatientCount * 1000, // in cents
  //     }
  //   : null

  // Use preview invoice if available, otherwise use actual upcomingInvoice
  // const invoiceToShow = previewInvoice || upcomingInvoice

  // Show patient activation guide for trial/inactive users with 0 patients AND 0 devices
  
  const shouldShowPatientGuide =
    (isTrial || isInactive || isTerminated || isFailed || isPastDue) &&
    activePatientCount === 0 &&
    deviceCount === 0

  // Handle portal session creation
  const handleManageBilling = async () => {
    setIsManagingBilling(true)
    try {
      // For failed subscriptions, skip subscription creation and go directly to portal
      // For active and canceled users, also go directly to portal
      // Others need to create a subscription first
      if (!hasUserBillingAccess(currentUser) && !isFailed) {
        try {
          const createResponse = await axios.post(API_ENDPOINTS.CREATE_SUBSCRIPTIONS())

          // Wait longer for Stripe to process subscription changes and cleanup
          await new Promise((resolve) => setTimeout(resolve, 1000))
        } catch (createError) {
          console.log('')

          const errorMessage =
            createError?.response?.data?.message ||
            'Failed to set up subscription. Please try again.'

          toast.error(errorMessage)
          setIsManagingBilling(false)
          return
        }
      }

      // Now open the portal (for all users) - New tab for better UX
      const response = await axios.post(API_ENDPOINTS.CREATE_PORTAL_SESSION())
      const {url} = response.data.data
      
      // Set flag to indicate we're going to Stripe (optimized - only flag needed)
      sessionStorage.setItem('stripe_portal_opened', 'true')
      console.log('🔄 Opening Stripe portal in new tab:', url)
      
      window.open(url, '_blank', 'noopener,noreferrer')
      setIsManagingBilling(false)
    } catch (error) {
      toast.error('Failed to open billing portal. Please try again.')
      setIsManagingBilling(false)
    }
  }

  return (
    <>
      {/* Management Section */}
      <div className='mb-4 p-4 border bg-white rounded-4 shadow-sm'>
        <h5 className='fw-bold mb-3 text-dark'>Manage Subscription</h5>

        <div className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3'>
          <div>
            <div className='d-flex align-items-center mb-2'>
              <p className='mb-0 me-2 text-dark'>Status:</p>
              {isTrial ? (
                <span className='badge bg-info text-uppercase px-3 py-2'>Trial</span>
              ) : isCancelled ? (
                <span className='badge bg-warning text-uppercase px-3 py-2'>Cancelled</span>
              ) : isTerminated ? (
                <span className='badge bg-danger text-uppercase px-3 py-2'>Terminated</span>
              ) : isInactive ? (
                <span className='badge bg-secondary text-uppercase px-3 py-2'>Inactive</span>
              ) : isFailed ? (
                <span className='badge bg-danger text-uppercase px-3 py-2'>Failed</span>
              ) : isPastDue ? (
                <span className='badge bg-warning text-uppercase px-3 py-2'>Past Due</span>
              ) : (
                <span className='badge bg-success text-uppercase px-3 py-2'>Active</span>
              )}
            </div>
            {/* active patient count */}
            <div className='d-flex align-items-center'>
              <p className='mb-0 me-2 text-dark'>Active Patients:</p>
              <span className='text-black px-1 py-1'>{activePatientCount}</span>
            </div>

            {/* device count */}
            <div className='d-flex align-items-center'>
              <p className='mb-0 me-2 text-dark'>Rental Devices:</p>
              <span className='text-black px-1 py-1'>{deviceCount || 0}</span>
            </div>

            {isTrial || isInactive ? (
              <div className='d-flex align-items-center'>
                <p className='mb-0 text-muted'>
                  <small>
                    {isTrial
                      ? "You're on a trial subscription with full access. To continue using all features after your trial, please subscribe."
                      : 'Your account is inactive. Subscribe to start monitoring patients.'}
                  </small>
                </p>
              </div>
            ) : isFailed ? (
              <p className='mb-0 text-muted text-sm text-gray-500'>
                <FaInfoCircle className='me-1 text-danger' />
                Your initial subscription payment failed. Please add a valid payment method to start
                your subscription.
              </p>
            ) : isPastDue ? (
              <p className='mb-0 text-muted text-sm text-gray-500'>
                <FaInfoCircle className='me-1 text-warning' />
                Your subscription renewal payment failed. You have limited access during the grace
                period. Please update your payment method to restore full service.
              </p>
            ) : isCancelled ? (
              <p className='mb-0 text-muted text-sm text-gray-500'>
                Your subscription has been cancelled. You will still have access until{' '}
                <strong className='text-black font-semibold bg-yellow-100 px-1 rounded'>
                  {subscriptionEndDate ? (() => {
                    // Handle different date formats
                    let date
                    if (typeof subscriptionEndDate === 'number') {
                      // Unix timestamp (seconds)
                      date = new Date(subscriptionEndDate * 1000)
                    } else if (typeof subscriptionEndDate === 'string') {
                      // ISO string or other string format
                      date = new Date(subscriptionEndDate)
                    } else {
                      return 'N/A'
                    }
                    
                    // Check if date is valid
                    if (isNaN(date.getTime())) {
                      return 'N/A'
                    }
                    
                    return date.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  })() : 'N/A'}
                </strong>
              </p>
            ) : isTerminated ? (
              <p className='mb-0 text-muted text-sm text-gray-500'>
                Your subscription has been terminated.
              </p>
            ) : subscriptionEndDate ? (
              <p className='mb-0 text-muted text-sm text-gray-500'>
                Your subscription will automatically renew on{' '}
                <strong className='text-black font-semibold bg-yellow-100 px-1 rounded'>
                  {(() => {
                    // Handle different date formats
                    let date
                    if (typeof subscriptionEndDate === 'number') {
                      // Unix timestamp (seconds)
                      date = new Date(subscriptionEndDate * 1000)
                    } else if (typeof subscriptionEndDate === 'string') {
                      // ISO string or other string format
                      date = new Date(subscriptionEndDate)
                    } else {
                      return 'N/A'
                    }
                    
                    // Check if date is valid
                    if (isNaN(date.getTime())) {
                      return 'N/A'
                    }
                    
                    return date.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  })()}
                </strong>
              </p>
            ) : null}
          </div>

          <div className='d-flex flex-column gap-2'>
            {/* All subscription management now goes through Stripe Portal */}
            {/* 
            {(isTrial || isInactive) ? (
              <div className='d-flex flex-column align-items-end'>
                <PrimaryButton
                  color='primary'
                  disabled={isLoading || activePatientCount === 0}
                  onClick={handleStartSubscription}
                  className='mt-2 mt-md-0'
                  title={activePatientCount === 0 ? 'Add active patients first to subscribe' : ''}
                >
                  {isLoading ? 'Processing...' : 'Subscribe Now'}
                </PrimaryButton>
                {activePatientCount === 0 && (
                  <small className='text-muted mt-1'>
                    <i className='fas fa-info-circle me-1'></i>
                    Add patients first to subscribe
                  </small>
                )}
              </div>
            ) : (isCancelled || isTerminated) ? (
              <PrimaryButton
                color='primary'
                disabled={isLoading}
                onClick={handleResubscribe}
                className='mt-2 mt-md-0'
              >
                {isLoading ? 'Processing...' : 'Resubscribe'}
              </PrimaryButton>
            ) : (
              <PrimaryButton
                color='danger'
                disabled={isCancelling}
                onClick={openCancelModal}
                className='mt-2 mt-md-0'
              >
                {isCancelling ? 'Processing...' : 'Cancel Subscription'}
              </PrimaryButton>
            )}
            */}

            {/* Only show portal button for subscription management */}
            {(activePatientCount > 0 ||
              deviceCount > 0 ||
              isActive ||
              isCancelled ||
              isFailed ||
              isPastDue) && (
              <PrimaryButton
                color='primary'
                onClick={handleManageBilling}
                className='mt-2'
                disabled={isManagingBilling}
              >
                {isManagingBilling ? (
                  <>
                    <FaSpinner className='me-2 fa-spin' />
                    Processing...
                  </>
                ) : (
                  <>
                    <FaExternalLinkAlt className='me-2' />
                    {isTrial || isInactive || isNeverSubscribed
                      ? 'Start Subscription'
                      : isFailed
                      ? 'Add Payment Method'
                      : isPastDue
                      ? 'Update Payment Method'
                      : isCancelled || isTerminated
                      ? 'Reactivate Subscription'
                      : 'Manage Subscription'}
                  </>
                )}
              </PrimaryButton>
            )}
          </div>
        </div>
      </div>

      {/* Patient Activation Guide for users with 0 patients AND 0 devices */}
      {(shouldShowPatientGuide && !loading) && (
        <div className='mb-4 p-4 border bg-light rounded-4 shadow-sm'>
          <div className='text-center'>
            <h5 className='fw-bold text-dark mb-3'>Add Patients or Devices to Get Started</h5>
            <p className='text-muted mb-4'>
              You currently have <strong>0 active patients</strong> and <strong>0 devices</strong>.
              To subscribe and start monitoring, you'll need to add patients or devices to your
              account first.
            </p>
            <div className='d-flex flex-column flex-md-row gap-3 justify-content-center align-items-center'>
              <NavLink to='/patient'>
                <button className='btn btn-primary btn-with-icon'>
                  <i className='fas fa-plus me-2'></i>
                  Add New Patients
                </button>
              </NavLink>
              <span className='text-muted'>or</span>
              <NavLink to='/patient?status=inactive'>
                <button className='btn btn-outline-primary'>
                  <i className='fas fa-toggle-on me-2'></i>
                  Activate Existing Patients
                </button>
              </NavLink>
            </div>
            <p className='text-sm text-muted mt-3 mb-0'>
              <i className='fas fa-info-circle me-1'></i>
              Once you have active patients or devices, return here to see your billing details and subscribe.
            </p>
          </div>
        </div>
      )}

      {/* Checkout Form - Commented out since using Stripe Portal */}
      {/* 
      {clientSecret && (
        <div className='mb-4'>
          <Elements stripe={stripePromise} options={{clientSecret}}>
            <CheckoutForm />
          </Elements>
        </div>
      )}
      */}

      {/* Payment Methods Section - Commented out to use Stripe Portal */}
      {/* 
      <div className='mb-4'>
        <PaymentMethods 
          paymentMethods={paymentMethods} 
          defaultPM={defaultPM} 
          fetchBillingData={fetchBillingData}
        />
      </div>
      */}

      {/* Upcoming Invoice Section - Commented out to use Stripe Portal */}
      {/* 
      <div>
        <UpcomingInvoice 
          upcomingInvoice={upcomingInvoice} 
          isTrial={isTrial} 
          isInactive={isInactive}
        />
      </div>
      */}

      {/* Invoice History Section - Commented out to use Stripe Portal */}
      {/* 
      <div className='mb-4'>
        <InvoiceHistory
          currentInvoices={currentInvoices}
          indexOfFirstInvoice={indexOfFirstInvoice}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
        />
      </div>
      */}
    </>
  )
}

export default BillingDetails
